import { useState, useEffect } from 'react';
import { useWheel } from '../context/WheelContext';

function RouletteWheel({ participants, spinning, onSpinEnd, onSelectWinner, winner }) {
  const { isSharkMode } = useWheel();
  const [rotation, setRotation] = useState(0);
  // Paleta de colores profesional para la ruleta
  const sharkColors = [
    '#0EA5E9', // Azul océano
    '#0369A1', // Azul profundo
    '#0C4A6E', // Azul marino
    '#164E63', // Azul tiburón
    '#083344', // Azul abismal
  ];
  const normalColors = [
    '#0ea5e9', // primary-500
    '#64748b', // secondary-500
    '#0284c7', // primary-600
    '#475569', // secondary-600
    '#0369a1', // primary-700
    '#334155', // secondary-700
    '#075985', // primary-800
    '#1e293b', // secondary-800
  ];
  const colors = isSharkMode ? sharkColors : normalColors;
  const centerX = 150;
  const centerY = 150;
  const radius = 140;

  useEffect(() => {
    if (spinning) {
      // Seleccionar ganador
      const winnerIndex = Math.floor(Math.random() * participants.length);
      
      // Calcular rotación para que el ganador quede exactamente arriba
      const segmentAngle = 360 / participants.length;
      // Cambiamos la dirección de rotación (sentido horario)
      const rotationToWinner = (90 + (winnerIndex * segmentAngle)) % 360;
      
      // Aumentamos el número de vueltas para más velocidad inicial
      const fullSpins = 2160; // 6 vueltas completas
      const finalRotation = -1 * (fullSpins + rotationToWinner); // Multiplicamos por -1 para girar en sentido horario
      
      // Reiniciamos la rotación antes de empezar el nuevo giro
      setRotation(0);
      // Usamos setTimeout para asegurar que el cambio a 0 se aplique antes de la nueva rotación
      setTimeout(() => {
        setRotation(finalRotation);
      }, 0);

      setTimeout(() => {
        onSelectWinner(participants[winnerIndex]);
        onSpinEnd();
        // Normalizamos la rotación final para el próximo giro
        setRotation(finalRotation % 360);
      }, 5000); // 5 segundos en total
    }
  }, [spinning, participants, onSpinEnd, onSelectWinner]);

  const getSlicePath = (index, total) => {
    const angle = (2 * Math.PI) / total;
    // Ajustamos el ángulo inicial para que el primer segmento esté centrado arriba
    const startAngle = index * angle + (Math.PI / 2) - (angle / 2);
    const endAngle = startAngle + angle;
    
    const start = {
      x: centerX + radius * Math.cos(startAngle),
      y: centerY + radius * Math.sin(startAngle)
    };
    
    const end = {
      x: centerX + radius * Math.cos(endAngle),
      y: centerY + radius * Math.sin(endAngle)
    };
    
    const largeArcFlag = angle > Math.PI ? 1 : 0;
    
    return `M ${centerX} ${centerY}
            L ${start.x} ${start.y}
            A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}
            Z`;
  };

  const getTextPosition = (index, total) => {
    const angle = (2 * Math.PI) / total;
    // Ajustamos el ángulo del texto para que esté centrado en su segmento
    const midAngle = index * angle + (Math.PI / 2);
    const textRadius = radius * 0.65;
    
    return {
      x: centerX + textRadius * Math.cos(midAngle),
      y: centerY + textRadius * Math.sin(midAngle),
      rotate: (midAngle * 180) / Math.PI + 90
    };
  };

  return (
    <div className="relative w-[400px] mx-auto flex items-center">
      {/* Ruleta */}
      <div className="relative w-[300px] h-[300px]">
        <svg
          viewBox="0 0 300 300"
          className="w-full h-full"
          style={{
            transform: `rotate(${rotation}deg)`,
            transition: spinning ? 'transform 5s cubic-bezier(0.2, 0.8, 0.1, 0.99)' : 'none'
          }}
        >
          <g>
            {participants.map((participant, index) => {
              const textPos = getTextPosition(index, participants.length);
              return (
                <g key={index}>
                  <path
                    d={getSlicePath(index, participants.length)}
                    fill={colors[index % colors.length]}
                    stroke="#f8fafc" // secondary-50
                    strokeWidth="2"
                    className="transition-colors duration-300"
                  />
                  <text
                    x={textPos.x}
                    y={textPos.y}
                    fill="white"
                    fontSize="14"
                    fontWeight="bold"
                    textAnchor="middle"
                    transform={`rotate(${textPos.rotate}, ${textPos.x}, ${textPos.y})`}
                    className="text-shadow select-none"
                  >
                    {participant}
                  </text>
                </g>
              );
            })}
          </g>
        </svg>

        {/* Centro de la ruleta */}
        <div className="absolute top-1/2 left-1/2 w-12 h-12 -mt-6 -ml-6 bg-secondary-800 rounded-full shadow-lg border-4 border-secondary-200 z-10">
          <div className="absolute inset-0 rounded-full bg-gradient-to-b from-secondary-700 to-secondary-900" />
        </div>
      </div>

      {/* Indicador */}
      <div className="relative h-full ml-[-20px]">
        <div className="absolute top-1/2 -translate-y-1/2">
          <div className="w-10 h-20 relative">
            <div 
              className="absolute left-0 top-1/2 -translate-y-1/2 w-0 h-0 
                border-t-[15px] border-t-transparent 
                border-r-[30px] border-r-primary-600
                border-b-[15px] border-b-transparent
                filter drop-shadow-md"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default RouletteWheel; 